import React, { JSX, useEffect, useState } from 'react';
import SliderTabs from '../shared/SliderTabs';
import { LocationItem } from 'components/types/LocationTypes';
import Icons from 'components/atoms/Icons';
import Modal from 'assets/shared/Modal';
import getOsfViewportSize from 'assets/utils/getOsfViewportSize';
import { myChart } from '../shared/MyChartConfig';
import { useTracking } from 'lib/tracking';

type DeptVisitTypeMap = {
  [deptId: string]: {
    visitTypeId: string;
    firstAvailableDate: string;
  };
};

type ProviderSchedulingWidgetProps = {
  providerHeading: string;
  providerEpicId: string;
  locations: Array<LocationItem>;
  deptDays: { [key: string]: string[] };
  deptVisitTypes: DeptVisitTypeMap;
  providerSchedulable: boolean;
};

declare global {
  interface Window {
    mychartWidget?: {
      renderWidget: (options: any) => void;
    };
  }
}

function loadWidgetScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') return resolve();

    if (document.querySelector(`script[src="${src}"]`)) {
      return resolve();
    }

    const script = document.createElement('script');

    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error(`Failed to load ${src}`));

    document.head.appendChild(script);
  });
}

const MYCHART_HOST = myChart.host;
const MYCHART_INSTANCE = myChart.instance;
const API_KEY = myChart.apiKey;

const WIDGET_SDK_SRC = `https://${myChart.host}/${myChart.instance}/Scripts/lib/Widget/widget_sdk.js`;

const ProviderSchedulingWidget = (
  props: ProviderSchedulingWidgetProps
): JSX.Element => {
  const {
    providerHeading,
    providerEpicId,
    locations,
    deptDays,
    deptVisitTypes,
    providerSchedulable,
  } = props;

  const hasSingleLocation = locations.length === 1;

  const singleLocation = hasSingleLocation
    ? locations[0]
    : null;

  const singleLocationDeptId =
    singleLocation?.fields?.EpicDeptId?.value;

  const singleLocationSchedulable =
    !!singleLocationDeptId &&
    deptDays[singleLocationDeptId]?.length > 0 &&
    !!deptVisitTypes[singleLocationDeptId]
      ?.visitTypeId;

  const [
    providerDeptSchedulable,
    setProviderDeptSchedulable,
  ] = useState(singleLocationSchedulable);

  const [locationsOpen, setLocationsOpen] =
    useState(false);

  const [
    selectedLocation,
    setSelectedLocation,
  ] = useState<LocationItem | null>(
    singleLocation
  );

  const [
    showLocationError,
    setShowLocationError,
  ] = useState(false);

  const { linksource } = useTracking();

  const deptId =
    selectedLocation?.fields?.EpicDeptId?.value;

  const vtId = deptId
    ? deptVisitTypes?.[deptId]?.visitTypeId
    : undefined;

  const isScheduleDisabled =
    !selectedLocation ||
    !providerSchedulable ||
    !providerDeptSchedulable ||
    !vtId;

  const selectedPhone =
    selectedLocation?.fields?.Phone?.value;

  const shouldShowCallScheduling =
    !providerSchedulable ||
    (!!selectedLocation &&
      !providerDeptSchedulable);

  const shouldShowOnlineScheduling =
    providerSchedulable &&
    (!selectedLocation ||
      (providerDeptSchedulable && !!vtId));

  const index = locations.findIndex(
    loc =>
      loc?.fields?.EpicDeptId?.value === deptId
  );

  const containerIdDesktop = `mychart-container-${providerEpicId}-${deptId}-${index}`;

  const containerIdMobile = `mychart-container-${providerEpicId}-${deptId}-${index}-mobile`;

  const baseUrl = `https://${MYCHART_HOST}/${MYCHART_INSTANCE}`;

  const initializeWidget = (
    containerId: string
  ) => {
    if (
      !window.mychartWidget ||
      !deptId ||
      !providerEpicId ||
      !vtId
    ) {
      return false;
    }

    const container =
      document.getElementById(containerId);

    if (!container) {
      return false;
    }

    try {
      container.innerHTML = '';

      window.mychartWidget.renderWidget({
        url: baseUrl,
        apiKey: API_KEY,
        widgetType: 'openscheduling',
        containerId,
        additionalParams: {
          vt: vtId,
          dept: deptId,
          id: providerEpicId,
          ...(linksource && { linksource }),
        },
      });

      console.log(
        `Widget initialized for container: ${containerId}`
      );

      return true;
    } catch (error) {
      console.error(
        `Failed to initialize widget for ${containerId}:`,
        error
      );

      return false;
    }
  };

  const tryInitBothContainers = () => {
    const containers = [
      containerIdDesktop,
      containerIdMobile,
    ];

    let initializedCount = 0;

    containers.forEach(containerId => {
      if (
        document.getElementById(containerId)
      ) {
        const success =
          initializeWidget(containerId);

        if (success) {
          initializedCount++;
        }
      }
    });

    return initializedCount;
  };

  useEffect(() => {
    if (
      !selectedLocation ||
      !deptId ||
      !vtId
    ) {
      return;
    }

    let isMounted = true;
    let retryCount = 0;

    const maxRetries = 50;

    loadWidgetScript(WIDGET_SDK_SRC)
      .then(() => {
        function tryInit() {
          if (!isMounted) return;

          if (window.mychartWidget) {
            const initializedCount =
              tryInitBothContainers();

            if (
              initializedCount === 0 &&
              retryCount < maxRetries
            ) {
              retryCount++;
              setTimeout(tryInit, 100);
            } else if (
              initializedCount === 0
            ) {
              console.warn(
                'Max retries reached. No widget containers found.'
              );
            }
          } else if (
            retryCount < maxRetries
          ) {
            retryCount++;
            setTimeout(tryInit, 100);
          }
        }

        tryInit();
      })
      .catch(error => {
        console.error(
          'Failed to load MyChart widget SDK:',
          error
        );
      });

    return () => {
      isMounted = false;
    };
  }, [
    containerIdDesktop,
    containerIdMobile,
    deptId,
    vtId,
    providerEpicId,
    selectedLocation,
    linksource,
  ]);

  useEffect(() => {
    if (
      !selectedLocation ||
      !deptId ||
      !vtId
    ) {
      return;
    }

    const handleScheduleButtonClick = (
      event: MouseEvent
    ) => {
      const target =
        event.target as HTMLElement;

      if (
        !target.closest('.schedule-btn')
      ) {
        return;
      }

      setTimeout(() => {
        initializeWidget(
          containerIdDesktop
        );
      }, 300);
    };

    document.addEventListener(
      'click',
      handleScheduleButtonClick
    );

    return () => {
      document.removeEventListener(
        'click',
        handleScheduleButtonClick
      );
    };
  }, [
    selectedLocation,
    deptId,
    vtId,
    containerIdDesktop,
    providerEpicId,
    linksource,
  ]);

  const selectLocation = (
    location: LocationItem
  ) => {
    const selectedDeptId =
      location?.fields?.EpicDeptId?.value;

    const hasDeptDates =
      deptDays[selectedDeptId]?.length > 0;

    const hasDeptVisitType =
      !!deptVisitTypes[selectedDeptId]
        ?.visitTypeId;

    setSelectedLocation(location);
    setShowLocationError(false);

    document
      .querySelector('.scheduling-widget')
      ?.querySelector('.error-msg')
      ?.classList.remove('shown');

    setProviderDeptSchedulable(
      hasDeptDates && hasDeptVisitType
    );

    setLocationsOpen(false);
  };

  const showMobileSchedule = () => {
    if (!selectedLocation) {
      setShowLocationError(true);
      return;
    }

    if (isScheduleDisabled) {
      return;
    }

    setShowLocationError(false);

    const showChart = () => {
      document
        .querySelector('.mychart-container')
        ?.classList.add('shown');

      document
        .querySelector('.slider-tabs')
        ?.classList.add('hidden');

      setTimeout(() => {
        initializeWidget(containerIdMobile);
      }, 300);
    };

    showChart();
  };

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleResize = () => {
      const viewportSize =
        getOsfViewportSize();

      if (
        viewportSize === 'lg' ||
        viewportSize === 'xl' ||
        viewportSize === 'xxl'
      ) {
        document
          .querySelector('.slider-tabs')
          ?.classList.remove('hidden');
      }
    };

    window.addEventListener(
      'resize',
      handleResize
    );

    return () => {
      window.removeEventListener(
        'resize',
        handleResize
      );
    };
  }, []);

  useEffect(() => {
    if (!hasSingleLocation) {
      return;
    }

    const location = locations[0];

    const selectedDeptId =
      location?.fields?.EpicDeptId?.value;

    const hasDeptDates =
      deptDays[selectedDeptId]?.length > 0;

    const hasDeptVisitType =
      !!deptVisitTypes[selectedDeptId]
        ?.visitTypeId;

    setSelectedLocation(location);

    setProviderDeptSchedulable(
      hasDeptDates && hasDeptVisitType
    );

    setShowLocationError(false);
    setLocationsOpen(false);
  }, [
    hasSingleLocation,
    locations,
    deptDays,
    deptVisitTypes,
  ]);

  return (
    <div className="scheduling-widget">
      <SliderTabs
        tabsText={[
          'New Patient',
          'Returning Patient',
        ]}
      >
        <>
          {!hasSingleLocation && (
            <div
              className="location-label-wrapper"
              role="region"
            >
              <p className="location-label">
                Location
              </p>

              <p
                className="error-msg"
                aria-live="polite"
              >
                Select location
              </p>
            </div>
          )}

          {!hasSingleLocation && (
            <div className="schedule-location-wrapper">
              <div
                className={`${
                  locationsOpen
                    ? 'schedule-location open'
                    : 'schedule-location'
                } ${
                  !selectedLocation
                    ? 'no-location-selected'
                    : ''
                } ${
                  showLocationError &&
                  !selectedLocation
                    ? 'location-error'
                    : ''
                }`}
                onClick={() => {
                  setShowLocationError(false);
                  setLocationsOpen(!locationsOpen);
                }}
              >
                <button
                  className="selector"
                  aria-invalid={
                    showLocationError &&
                    !selectedLocation
                  }
                  aria-describedby={
                    showLocationError &&
                    !selectedLocation
                      ? 'location-selection-error'
                      : undefined
                  }
                  aria-label={
                    selectedLocation
                      ? 'Location Selector: Selected: ' +
                        selectedLocation?.fields?.City
                          ?.value +
                        ': ' +
                        selectedLocation?.fields
                          ?.LocationName?.value
                      : 'Location Selector: Choose your location'
                  }
                >
                  <p className="default">
                    {selectedLocation
                      ? selectedLocation?.fields?.City
                          ?.value +
                        ': ' +
                        selectedLocation?.fields
                          ?.LocationName?.value
                      : '- Select Location -'}
                  </p>

                  {locationsOpen ? (
                    <Icons id="chevron-up" />
                  ) : (
                    <Icons id="chevron-down" />
                  )}
                </button>
              </div>

              <div
                className={`${
                  locationsOpen
                    ? 'location-options shown'
                    : 'location-options'
                }`}
              >
                {locations?.map(
                  (location, index) => {
                    return (
                      <p
                        key={`option-${index}`}
                        onClick={() =>
                          selectLocation(location)
                        }
                        onKeyDown={e =>
                          e.key === 'Enter' ||
                          e.key === ' '
                            ? selectLocation(location)
                            : ''
                        }
                        tabIndex={
                          locationsOpen ? 0 : -1
                        }
                      >
                        {index +
                          1 +
                          '. ' +
                          location?.fields?.City
                            ?.value +
                          ': ' +
                          location?.fields
                            ?.LocationName?.value}
                      </p>
                    );
                  }
                )}
              </div>
            </div>
          )}

          {!hasSingleLocation &&
            showLocationError &&
            !selectedLocation && (
              <div
                id="location-selection-error"
                className="location-selection-error"
                role="alert"
              >
                <span className="icon icon-location">
                  <Icons id="icon-error-circle-filled" />
                </span>

                <p>
                  Choose a location to continue
                </p>
              </div>
            )}

          {shouldShowOnlineScheduling && (
            <div
              className="inner-content shown"
              id="online-scheduling"
            >
              <button
                type="button"
                className={`btn-primary mobile drawer-btn ${
                  isScheduleDisabled
                    ? 'is-disabled'
                    : ''
                }`}
                aria-disabled={isScheduleDisabled}
                onClick={showMobileSchedule}
              >
                Schedule Online
              </button>
            </div>
          )}

          {shouldShowCallScheduling && (
            <div
              className="inner-content shown"
              id="call-scheduling"
            >
              <div className="specialty-scheduling">
                {selectedLocation && (
                  <p className="referral-text">
                    Not sure if you need a
                    referral? Contact your
                    insurance company before
                    scheduling to see if you need
                    a referral from your primary
                    care physician.
                  </p>
                )}

                {!selectedLocation && (
                  <button
                    type="button"
                    className="call-btn btn-primary is-disabled"
                    aria-disabled="true"
                    onClick={() => {
                      setShowLocationError(true);
                    }}
                  >
                    Call to Schedule
                  </button>
                )}

                {selectedLocation &&
                  selectedPhone && (
                    <a
                      href={`tel:${selectedPhone}`}
                      className="call-btn btn-primary shown"
                    >
                      {selectedPhone}
                    </a>
                  )}
              </div>
            </div>
          )}

          {shouldShowOnlineScheduling &&
            (selectedLocation &&
            providerDeptSchedulable &&
            vtId ? (
              <Modal
                customClass="scheduling-modal"
                openBtnClass="btn-primary schedule-btn"
                openBtnLabel="Schedule Online"
                portalId="sidebar-modal-root"
              >
                <div className="modal-details">
                  <h2>{providerHeading}</h2>

                  <p className="subtitle">
                    New Patient Appointment
                  </p>

                  <p className="location-label">
                    Location
                  </p>

                  <div className="schedule-location">
                    <div className="selector">
                      <p className="scheduling-modal-location">
                        {selectedLocation
                          ?.fields?.City?.value +
                          ': ' +
                          selectedLocation
                            ?.fields
                            ?.LocationName?.value}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mychart-container">
                  <div
                    className="epic-widget-ctabox"
                    id={containerIdDesktop}
                    data-url={baseUrl}
                    data-apikey={API_KEY}
                    data-widget-type="openscheduling"
                    data-additionalparams-dept={
                      deptId
                    }
                    data-additionalparams-vt={
                      vtId
                    }
                    data-additionalparams-id={
                      providerEpicId
                    }
                    data-additionalparams-linksource={
                      linksource
                    }
                  ></div>
                </div>
              </Modal>
            ) : (
              <button
                type="button"
                className={`btn-primary schedule-btn desktop ${
                  isScheduleDisabled
                    ? 'is-disabled'
                    : ''
                }`}
                aria-disabled={isScheduleDisabled}
                onClick={() => {
                  if (!selectedLocation) {
                    setShowLocationError(true);
                  }
                }}
              >
                Schedule Online
              </button>
            ))}
        </>

        <>
          <div className="inner-content">
            <p className="log-in osf-intro-copy">
              Log in to OSF MyChart to
              Schedule
            </p>

            <a
              href="https://osfmychart.org/OSFMyChart/Authentication/Login?postloginurl=scheduling"
              target="_blank"
              className="mychart-btn btn-primary"
            >
              OSF MyChart
            </a>
          </div>
        </>
      </SliderTabs>

      <div className="mychart-container">
        <div
          className="epic-widget-ctabox"
          id={containerIdMobile}
          data-url={baseUrl}
          data-apikey={API_KEY}
          data-widget-type="openscheduling"
          data-additionalparams-dept={deptId}
          data-additionalparams-vt={vtId}
          data-additionalparams-id={
            providerEpicId
          }
          data-additionalparams-linksource={
            linksource
          }
        ></div>

        <button
          className="back-btn btn-secondary"
          onClick={() => {
            document
              .querySelector(
                '.mychart-container'
              )
              ?.classList.remove('shown');

            document
              .querySelector('.slider-tabs')
              ?.classList.remove('hidden');
          }}
        >
          Back
        </button>
      </div>
    </div>
  );
};

export default ProviderSchedulingWidget;